import { useState } from 'react';

import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconReplace } from '@components/icons/replace';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCompareWithCurrent, useRestoreVersion } from 'api/journeys/hooks';

import { ActionChangesSection } from './version-helpers';

export type RestoreVersionContentProps = {
  journeyShortId: string;
  versionId: string;
  hasUnpublishedChanges: boolean;
  onBack?: () => void;
  onSuccess?: () => void;
};

export const RestoreVersionContent = ({
  journeyShortId,
  versionId,
  onBack,
  onSuccess,
}: RestoreVersionContentProps) => {
  const { t } = useScopedTranslate(locale);
  const [confirm, setConfirm] = useState(false);

  const restoreVersion = useRestoreVersion(journeyShortId);
  const { data: comparison, isLoading } = useCompareWithCurrent(journeyShortId, versionId);

  const summary = comparison?.summary;
  const hasChanges = summary && (summary.added > 0 || summary.removed > 0 || summary.modified > 0);

  const handleRestore = async () => {
    await restoreVersion.mutateAsync({ versionId, confirm });
    onSuccess?.();
  };

  return (
    <Stack space={6}>
      <Stack space={4}>
        <Text font="regular14" text={t('restoreVersionDescription')} />
        <Text font="regular14" color="elementRed200" text={t('currentChangesWillBeLost')} />
      </Stack>

      {isLoading && <Text text={t('loadingComparison')} />}

      {hasChanges && summary && comparison && (
        <>
          <Stack space={3}>
            <Stack orientation="horizontal" justify="space-between">
              <Text font="bold14" color="textPrimary" text={t('changesPreview')} />
              <Badge text={summary.added + summary.removed + summary.modified} />
            </Stack>
            <Separator />
            <ActionChangesSection
              summary={summary}
              addedActions={comparison.addedActions}
              modifiedActions={comparison.modifiedActions}
              removedActions={comparison.removedActions}
            />
          </Stack>
        </>
      )}
      <Stack space={2}>
        <Text font="semibold14" text={t('pleaseConfirm')} />
        <Checkbox label={t('iUnderstand')} onChange={(value) => setConfirm(value)} />
      </Stack>

      <Sidebar.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button variant="secondary" onClick={onBack} text={t('core.cancel')} />
          <Button
            isDisabled={!confirm}
            onClick={handleRestore}
            disabled={restoreVersion.isLoading}
            iconLeft={<IconReplace />}
            variant="primary"
            text={t('restore')}
          />
        </Stack>
      </Sidebar.Footer>
    </Stack>
  );
};

export const locale = {
  en: {
    restoreVersion: 'Restore version',
    restoreVersionDescription:
      'This will restore your journey draft to the selected published version.',
    versionInfo: 'Version information',
    versionDetails: 'Version {versionNumber} published on {dateCreated}',
    currentChangesWillBeLost:
      'Any unpublished changes in your current draft will be permanently lost.',
    loadingComparison: 'Loading version comparison...',
    changesPreview: 'Changes that will be applied',
    willBeAdded: '{count} actions will be added',
    willBeRemoved: '{count} actions will be removed',
    willBeModified: '{count} actions will be modified',
    actionsToAdd: 'Actions to add ({count})',
    actionsToRemove: 'Actions to remove ({count})',
    actionsToModify: 'Actions to modify ({count})',
    pleaseConfirm: 'Please confirm',
    iUnderstand: 'I understand that this will replace my current draft',
    restore: 'Restore',
    backToVersionHistory: 'Back to version history',
  },
};
